import React, { useContext, useMemo, useRef, useState } from 'react';
import { getAxiosTokenInstance } from '../../utils/axiosInstance';
import { useMutation, useQuery } from 'react-query';
import { FirebaseContext } from '../../context/FirebaseContext';
import { getCashBook } from '../../NecttosComp/APICaller/ApiServices';
import moment from 'moment';
import { Body, Header, Main } from '../../NecttosComp/Layout/Layout';
import Button from '../../NecttosComp/Button/Button';
import Input2 from '../../NecttosComp/Input/Input2';
import Instruction from '../../Documentation/Instruction';

const exportPdf = async ({ olddata, setLoading }: any) => {
	try {
		const instance = await getAxiosTokenInstance();
		const { collegeId, date, type, subType, endDate } = olddata;
		const resp = await instance.post('/college/reportGeneration/cashBooks', { collegeId, date, type, subType, endDate }, { responseType: 'blob' });
		if (resp.data.statusCode === 400) {
			setLoading(false);
			throw new Error(resp.data.message || 'API Error');
		}
		return resp.data;
	} catch (error) {
		setLoading(false);
		console.error(error);
		throw new Error('API Error');
	}
};

export const CashBook = ({ onClose }: any) => {
	const { collegeId, collegeList } = useContext(FirebaseContext);
	const componentRef = useRef<HTMLDivElement | null>(null);
	const selectedCollege = collegeList?.find((x: any) => x.collegeId === collegeId);
	const [date, setDate] = useState({ date: '', type: 'Government', subType: '', endDate: '' });
	const { data = {}, isFetching, refetch } = useQuery(['getCashBoook', date.type, date.subType], getCashBook(collegeId, date.date, date.type, date.subType, date.endDate));
	const [isLoading, setIsLoading] = useState(false);
	const dayWiseCashBook = useMemo(() => data?.dayWiseCashBook || [], [data]);
	const toAccounts = useMemo(() => data?.types || [], [data]);
	const toSubAccounts = useMemo(() => ['All Accounts', ...(data?.subTypes || [])], [data]);
	const heads = useMemo(() => data?.subType || data?.type, [data]);
	const exportPdfMutation = useMutation(exportPdf);
	const onExportExam = async () => {
		try {
			setIsLoading(true);
			const link = document.createElement('a');
			link.target = '_blank';
			link.download = `CashBook ${moment().format('DD MM YYYY hh mm A')}`;
			const pdfData = await exportPdfMutation.mutateAsync({
				olddata: { collegeId, date: date.date, type: date.type, subType: date.subType, endDate: date.endDate },
			});
			link.href = URL.createObjectURL(new Blob([pdfData], { type: 'application/pdf' }));
			link.click();
		} catch (e) {
			console.error(e);
		} finally {
			setIsLoading(false);
		}
	};

	const CashTable = ({ title, columns, data, totalValue, colWidths, renderRow }: any) => (
		<div className='mb-6 break-inside-avoid-page print:break-inside-avoid dark:text-white'>
			<h2 className='text-lg font-semibold mb-2 border-b border-gray-300 dark:border-gray-700 pb-1 print:text-black print:font-semibold print:mb-1 print:text-base dark:text-white'>{title}</h2>
			<table className='w-full table-fixed border-collapse text-sm print:text-[11px]' style={{ tableLayout: 'fixed' }}>
				<colgroup>
					{colWidths.map((w: any, i: any) => (
						<col key={i} style={{ width: w }} />
					))}
				</colgroup>
				<thead className='bg-gray-100 dark:bg-gray-800'>
					<tr>
						{columns.map((col: any, i: any) => (
							<th key={i} className='border border-gray-300 dark:border-gray-600 px-2 py-1 text-left'>
								{col}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{data.map((item: any, i: any) => (
						<tr key={i} className={i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'}>
							{renderRow(item, i).map((val: any, idx: any) => (
								<td key={idx} className={`border border-gray-300 dark:border-gray-600 px-2 py-1 ${idx === columns.length - 1 ? 'text-right' : 'text-left '} print:text-black print:px-1 print:py-0.5`}>
									{val}
								</td>
							))}
						</tr>
					))}
				</tbody>
				<tfoot className='bg-gray-100 dark:bg-gray-800 font-semibold'>
					<tr>
						<td colSpan={columns.length - 1} className='border border-gray-300 dark:border-gray-600 px-2 py-1 text-left dark:text-white'>
							Total
						</td>
						<td className='border border-gray-300 dark:border-gray-600 px-2 py-1 text-right dark:text-white'> {totalValue?.toFixed(2)}</td>
					</tr>
				</tfoot>
			</table>
		</div>
	);

	return (
		<Main width='100vw' height='100vh' title='Cash Book'>
			<Header className='sticky top-0 z-50 bg-white dark:bg-gray-900 shadow-md'>
				<div className='flex flex-wrap justify-between items-center gap-4 px-4 py-2'>
					<div className='flex flex-wrap items-center gap-2'>
						<Input2 fieldName='Main Head' options={toAccounts} returnKey='type' state={date} setState={setDate} type='drop' className='min-w-[180px]' />
						<Input2 fieldName='Sub Head' options={toSubAccounts} returnKey='subType' state={date} setState={setDate} type='drop' className='min-w-[180px]' />
						<Input2 fieldName='Start Date' returnKey='date' state={date} setState={setDate} type='date' className='min-w-[160px]' />
						<Input2 fieldName='End Date' returnKey='endDate' state={date} setState={setDate} type='date' className='min-w-[160px]' />
					</div>
					<div className='flex items-center gap-2'>
						<Button type='fetch' onClick={() => refetch()}>
							Load
						</Button>
						<Button type='download' onClick={onExportExam}>
							Download
						</Button>
						<Button type='print' tableRef={componentRef.current}>
							Print
						</Button>
						<Instruction keyId={'cashBook'} />
						<Button type='close' onClick={onClose}>
							Close
						</Button>
					</div>
				</div>
			</Header>

			<Body height='90vh'>
				<div className='relative h-full'>
					{(isFetching || isLoading) && (
						<div className='absolute inset-0 bg-white dark:bg-gray-900 bg-opacity-75 z-50 flex items-center justify-center'>
							<div className='animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500'></div>
						</div>
					)}
					<div ref={componentRef} id='printable' className='h-full  overflow-auto printable p-4 dark:bg-[#242424]  text-[13px] leading-relaxed font-sans print:text-black print:leading-tight print:text-[12px]'>
						{dayWiseCashBook.map((dayWise: any, index: any) => (
							<>
								{(dayWiseCashBook.length === 1 || parseInt(dayWise?.payments?.length) + parseInt(dayWise?.reciept?.length) > 0) && (
									<section key={index} className='break-after-page print:break-after-page pb-8 pt-8'>
										<header className='mb-4 w-full flex flex-row items-center rounded-3xl gap-4 print:gap-2 print:mb-2'>
											<img src={selectedCollege?.logo} alt='College Logo' className='w-20 h-20 object-contain rounded-xl print:w-16 print:h-16' />
											<div className='flex flex-col text-center flex-grow'>
												<h1 className='text-3xl font-extrabold text-gray-800 dark:text-white mb-1 print:text-xl print:font-bold print:text-black'>{heads} Cash Book</h1>
												<div className='text-sm text-gray-600 dark:text-gray-300 space-y-0.5 print:text-xs print:text-black'>
													<div className='font-medium text-lg print:font-semibold'>{selectedCollege?.collegeShortName}</div>
													<div>
														{selectedCollege?.address}, Phone: {selectedCollege?.phoneNumber}
													</div>
													<div className='text-xs text-gray-500 dark:text-gray-400 print:text-[11px]'>As on {moment(dayWise.date).format('dddd, DD MMM YYYY')}</div>
												</div>
											</div>
										</header>

										<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
											<div>
												<CashTable title='Opening Balance' columns={['SN', 'Account', 'Amount']} data={dayWise?.opeingBalance || []} totalValue={dayWise?.totals?.opeingBalance || 0} colWidths={['10%', '70%', '20%']} renderRow={(item: any, i: any) => [i + 1, item.item, item.amount != null ? item.amount.toFixed(2) : '']} />
												<CashTable title='Collection / Receipts' columns={['SN', 'Collection', 'Account Head', 'Amount']} data={dayWise?.reciept || []} totalValue={dayWise?.totals?.reciept || 0} colWidths={['8%', '25%', '42%', '25%']} renderRow={(item: any, i: any) => [i + 1, item.item, item.naration, item.amount != null ? item.amount.toFixed(2) : '']} />
											</div>
											<div>
												<CashTable title='Payments' columns={['SN', 'Payment', 'Narration', 'Amount']} data={dayWise?.payments || []} totalValue={dayWise?.totals?.payments || 0} colWidths={['8%', '25%', '42%', '25%']} renderRow={(item: any, i: any) => [i + 1, item.item, item.naration, item.amount != null ? item.amount.toFixed(2) : '']} />
												<CashTable title='Closing Balance' columns={['SN', 'Account', 'Amount']} data={dayWise?.closingBalance || []} totalValue={dayWise?.totals?.closingBalance || 0} colWidths={['10%', '70%', '20%']} renderRow={(item: any, i: any) => [i + 1, item.item, item.amount != null ? item.amount.toFixed(2) : '']} />
												{dayWise?.refund?.length > 0 && <CashTable title='Refund' columns={['SN', 'Account', 'Amount']} data={dayWise?.refund || []} totalValue={dayWise?.totals?.refund || 0} colWidths={['10%', '70%', '20%']} renderRow={(item: any, i: any) => [i + 1, item.item, item.amount != null ? item.amount.toFixed(2) : '']} />}
												<p className='text-right font-bold text-lg print:text-base print:font-semibold print:text-black dark:text-white'>Grand Total: {(dayWise?.totals?.grandTotalRecipt || 0).toFixed(2)}</p>
											</div>
										</div>
									</section>
								)}
							</>
						))}
					</div>
				</div>
			</Body>
		</Main>
	);
};
